import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import BrandThemeProvider from './theme/BrandThemeProvider';
import { DEFAULT_BRAND_COLORS } from './theme/brandTokens';
import LoginPage from './pages/auth/LoginPage';
import TeamMemberLoginPage from './pages/auth/TeamMemberLoginPage';
import AdminLoginPage from './pages/auth/AdminLoginPage';
import CustomerRegisterPage from './pages/auth/CustomerRegisterPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import SetPasswordPage from './pages/auth/SetPasswordPage';
import StoreRegistrationPage from './pages/stores/StoreRegistrationPage';
import StoresPage from './pages/stores/StoresPage';
import StoreHubRedirect from './pages/stores/StoreHubRedirect';
import StoreUpdatePage from './pages/stores/StoreUpdatePage';
import PaymentSettingsPage from './pages/stores/PaymentSettingsPage';
import CommunicationSettingsPage from './pages/stores/CommunicationSettingsPage';
import WaitlistOfferPage from './pages/customer/WaitlistOfferPage';
import WaitlistOfferClaimPage from './pages/customer/WaitlistOfferClaimPage';
import DataImportPage from './pages/stores/DataImportPage';
import EmployeePayrollDashboardPage from './pages/employee-payments/EmployeePayrollDashboardPage';
import TeamMemberCompensationPage from './pages/employee-payments/TeamMemberCompensationPage';
import TeamMembersPage from './pages/team-members/TeamMembersPage';
import OwnerProfilePage from './pages/team-members/OwnerProfilePage';
import TeamMemberViewPage from './pages/team-members/TeamMemberViewPage';
import TeamMembersFallbackPage from './pages/team-members/TeamMembersFallbackPage';
import TeamMemberRegistrationPage from './pages/team-members/TeamMemberRegistrationPage';
import TeamMemberUpdatePage from './pages/team-members/TeamMemberUpdatePage';
import TeamMemberAgendaPage from './pages/team-members/TeamMemberAgendaPage';
import ServicesPage from './pages/services/ServicesPage';
import ServicesFallbackPage from './pages/services/ServicesFallbackPage';
import ProductsPage from './pages/products/ProductsPage';
import ProductsFallbackPage from './pages/products/ProductsFallbackPage';
import { StockToProductsRedirect } from './pages/stock/StockPage';
import ExpensesPage from './pages/expenses/ExpensesPage';
import ExpensesFallbackPage from './pages/expenses/ExpensesFallbackPage';
import MembershipPlansPage from './pages/membership-plans/MembershipPlansPage';
import MembershipPlansFallbackPage from './pages/membership-plans/MembershipPlansFallbackPage';
import StoreCustomerMembershipsPage from './pages/customer-memberships/StoreCustomerMembershipsPage';
import StoreCustomersPage from './pages/customers/StoreCustomersPage';
import StoreCustomerDetailsPage from './pages/customers/StoreCustomerDetailsPage';
import CustomersFallbackPage from './pages/customers/CustomersFallbackPage';
import StaffNotificationsPage from './pages/notifications/StaffNotificationsPage';
import PaymentOperationsPage from './pages/payment-operations/PaymentOperationsPage';
import StoresManagementPage from './pages/stores-management/StoresManagementPage';
import ManagementDashboardPage from './pages/dashboard/ManagementDashboardPage';
import ManagementAppointmentsPage from './pages/appointments/ManagementAppointmentsPage';
import BookingPage from './pages/customer/BookingPage';
import MyAppointmentsPage from './pages/customer/MyAppointmentsPage';
import StoreMembershipsPage from './pages/customer/StoreMembershipsPage';
import StorePackagesPage from './pages/customer/StorePackagesPage';
import CustomerConsentsPage from './pages/customer/CustomerConsentsPage';
import CustomerProfilePage from './pages/customer/CustomerProfilePage';
import CustomerSettingsPage from './pages/customer/CustomerSettingsPage';
import CustomerNotificationsPage from './pages/customer/CustomerNotificationsPage';
import StoreDetailPage from './pages/customer/StoreDetailPage';
import NotFoundPage from './pages/common/NotFoundPage';
import ShortLinkRedirectPage from './pages/common/ShortLinkRedirectPage';
import LandingPage from './pages/marketing/LandingPage';
import MarketingLanguageSync from './components/seo/MarketingLanguageSync';
import { MARKETING_LANGUAGES } from './constants/marketingSeo';
import { PrivacyPolicyPage, CookiePolicyPage, TermsOfServicePage } from './pages/legal/LegalPages';
import { CookieConsentProvider } from './context/CookieConsentContext';
import CookieConsentBanner from './components/legal/CookieConsentBanner';
import UserTypeProtectedRoute from './components/auth/UserTypeProtectedRoute';
import ManagementRoute from './components/auth/ManagementRoute';
import PlanFeatureGuard from './components/auth/PlanFeatureGuard';
import RedirectIfAuthenticated from './components/auth/RedirectIfAuthenticated';
import { AuthProvider } from './context/AuthContext';
import { AppDataProvider } from './context/AppDataContext';
import { StoreProvider } from './context/StoreContext';
import { ToastProvider } from './context/ToastContext';
import { StaffRealtimeProvider } from './context/StaffRealtimeContext';
import { SupportChatProvider } from './context/SupportChatContext';
import SupportInboxPage from './pages/support/SupportInboxPage';
import AppLayout from './components/layout/AppLayout';
import CustomerLayout from './components/layout/CustomerLayout';
import CustomerBrandTheme from './components/branding/CustomerBrandTheme';
import StoreFaviconSync from './components/branding/StoreFaviconSync';
import StoreIdGuard from './components/auth/StoreIdGuard';
import StoreNotFoundGuard from './components/auth/StoreNotFoundGuard';
import ToastContainer from './components/common/ToastContainer';
import PwaPullToReload from './components/common/PwaPullToReload';
import { useAppData } from './context/AppDataContext';
import { Permissions } from './constants/Permissions';
import { getStoreManagementRedirect, getTeamMembersRedirect } from './utils/permissionRedirectUtils';
import { useTranslation } from 'react-i18next';
import { isSubdomainStoreMode } from './utils/storeUtils';
import DefaultDocumentHead from './components/seo/DefaultDocumentHead';

const appointmentsPageTitle = 'Appointments';
const appointmentsPageSubtitle = 'Manage your team agenda and booked appointments';
const agendaPermissions = [Permissions.TeamMembersAgendaRead, Permissions.TeamMembersAgendaReadOwn];

function ManagementAppointmentsRoute() {
  const { currentStore } = useAppData();

  return (
    <ManagementRoute
      pageTitle={appointmentsPageTitle}
      pageSubtitle={appointmentsPageSubtitle}
      skeletonVariant="agenda"
      storeId={currentStore?.id}
      permissions={agendaPermissions}
      redirectTo="/management/stores"
    >
      <ManagementAppointmentsPage />
    </ManagementRoute>
  );
}

const dashboardPageTitle = 'Dashboard';
const dashboardPageSubtitle = 'Overview of appointments, sales, and store performance';

function ManagementLandingRoute() {
  const { currentStore } = useAppData();

  return (
    <ManagementRoute
      pageTitle={dashboardPageTitle}
      pageSubtitle={dashboardPageSubtitle}
      skeletonVariant="cards"
      storeId={currentStore?.id}
    >
      <ManagementDashboardPage />
    </ManagementRoute>
  );
}

const selfServiceRegistrationElement = (
  <RedirectIfAuthenticated redirectTo="/management">
    <StoreRegistrationPage mode="self-service" />
  </RedirectIfAuthenticated>
);

// Marketing pages under a language prefix (/en, /en/privacy, ...). The unprefixed
// routes serve the default language; see constants/marketingSeo.js.
const renderLocalizedMarketingRoutes = () =>
  MARKETING_LANGUAGES.map((lang) => (
    <Route key={lang} path={`/${lang}`}>
      <Route index element={<LandingPage />} />
      <Route path="register-store" element={selfServiceRegistrationElement} />
      <Route path="privacy" element={<PrivacyPolicyPage />} />
      <Route path="cookies" element={<CookiePolicyPage />} />
      <Route path="terms" element={<TermsOfServicePage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  ));

// In subdomain store mode the storeId is implied by the host, so customer
// routes are mounted at the root and the StoreIdGuard (which expects /stores/
// :storeId) is skipped - StoreProvider resolves the store from the slug.
const CustomerProtectedLayout = () => (
  <UserTypeProtectedRoute allowedUserTypes={[3]}>
    <CustomerLayout>
      <Outlet />
    </CustomerLayout>
  </UserTypeProtectedRoute>
);

// Store subdomain hosts ({slug}.salovare.com) only serve the customer
// storefront; the storeId stays out of the URL.
function SubdomainRoutes() {
  return (
    <StoreProvider>
      <CustomerBrandTheme>
        <StoreNotFoundGuard>
          <Routes>
            <Route path="/login" element={
              <RedirectIfAuthenticated>
                <LoginPage />
              </RedirectIfAuthenticated>
            } />
            <Route path="/register" element={
              <RedirectIfAuthenticated>
                <CustomerRegisterPage />
              </RedirectIfAuthenticated>
            } />
            <Route path="/reset-password" element={
              <RedirectIfAuthenticated>
                <ResetPasswordPage />
              </RedirectIfAuthenticated>
            } />
            <Route path="/set-password" element={
              <RedirectIfAuthenticated>
                <SetPasswordPage />
              </RedirectIfAuthenticated>
            } />
            <Route path="/waitlist/offers/:token" element={<WaitlistOfferPage />} />
            <Route element={<CustomerProtectedLayout />}>
              <Route path="/" element={<StoreDetailPage />} />
              <Route path="/booking" element={<BookingPage />} />
              <Route path="/waitlist/offers/claim/:offerId" element={<WaitlistOfferClaimPage />} />
              <Route path="/my-appointments" element={<MyAppointmentsPage />} />
              <Route path="/memberships" element={<StoreMembershipsPage />} />
              <Route path="/packages" element={<StorePackagesPage />} />
              <Route path="/consents" element={<CustomerConsentsPage />} />
              <Route path="/customer-profile" element={<CustomerProfilePage />} />
              <Route path="/notifications" element={<CustomerNotificationsPage />} />
              <Route path="/settings" element={<CustomerSettingsPage />} />
            </Route>
            <Route path="/l/:token" element={<ShortLinkRedirectPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/not-found" element={<NotFoundPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </StoreNotFoundGuard>
      </CustomerBrandTheme>
    </StoreProvider>
  );
}

function App() {
  const { t } = useTranslation();
  const subdomainMode = isSubdomainStoreMode();

  return (
    <BrandThemeProvider colors={DEFAULT_BRAND_COLORS}>
      <CssBaseline />
      <AuthProvider>
        <AppDataProvider>
          <ToastProvider>
            <CookieConsentProvider>
            <Router>
            <StaffRealtimeProvider>
            <SupportChatProvider>
            <DefaultDocumentHead />
            <StoreFaviconSync />
            {!subdomainMode && <MarketingLanguageSync />}
            {subdomainMode ? <SubdomainRoutes /> : (
            <Routes>
              {/* Customer store routes - single StoreProvider so store name survives login → store navigation */}
              <Route path="/stores/:storeId" element={
                <StoreIdGuard>
                  <StoreProvider>
                    <CustomerBrandTheme>
                      <StoreNotFoundGuard>
                        <Outlet />
                      </StoreNotFoundGuard>
                    </CustomerBrandTheme>
                  </StoreProvider>
                </StoreIdGuard>
              }>
                <Route path="login" element={
                  <RedirectIfAuthenticated>
                    <LoginPage />
                  </RedirectIfAuthenticated>
                } />
                <Route path="register" element={
                  <RedirectIfAuthenticated>
                    <CustomerRegisterPage />
                  </RedirectIfAuthenticated>
                } />
                <Route path="set-password" element={
                  <RedirectIfAuthenticated>
                    <SetPasswordPage />
                  </RedirectIfAuthenticated>
                } />
                <Route path="waitlist/offers/:token" element={<WaitlistOfferPage />} />
                <Route element={<CustomerProtectedLayout />}>
                  <Route index element={<StoreDetailPage />} />
                  <Route path="booking" element={<BookingPage />} />
                  <Route path="waitlist/offers/claim/:offerId" element={<WaitlistOfferClaimPage />} />
                  <Route path="my-appointments" element={<MyAppointmentsPage />} />
                  <Route path="memberships" element={<StoreMembershipsPage />} />
                  <Route path="packages" element={<StorePackagesPage />} />
                  <Route path="consents" element={<CustomerConsentsPage />} />
                  <Route path="customer-profile" element={<CustomerProfilePage />} />
                  <Route path="notifications" element={<CustomerNotificationsPage />} />
                  <Route path="settings" element={<CustomerSettingsPage />} />
                </Route>
                <Route path="*" element={<Navigate to=".." replace relative="path" />} />
              </Route>

              {/* Management Auth Routes */}
              <Route path="/management/team-member/login" element={
                <RedirectIfAuthenticated redirectTo="/management">
                  <TeamMemberLoginPage />
                </RedirectIfAuthenticated>
              } />
              <Route path="/management/app-admin/login" element={
                <RedirectIfAuthenticated redirectTo="/management">
                  <AdminLoginPage />
                </RedirectIfAuthenticated>
              } />
              <Route path="/reset-password" element={
                <RedirectIfAuthenticated>
                  <ResetPasswordPage />
                </RedirectIfAuthenticated>
              } />
              <Route path="/set-password" element={
                <RedirectIfAuthenticated>
                  <SetPasswordPage />
                </RedirectIfAuthenticated>
              } />
              <Route path="/register-store" element={selfServiceRegistrationElement} />
              
              {/* Management Routes - Only accessible by AppAdmin (1) and TeamMember (2) */}
              <Route path="/management" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementLandingRoute />
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/appointments" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementAppointmentsRoute />
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/register" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle={t('storeRegistration.title')}
                    pageSubtitle={t('storeRegistration.subtitle')}
                    permissions={[Permissions.StoresCreate]}
                    redirectTo="/management/stores"
                  >
                    <StoreRegistrationPage mode="admin" />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <AppLayout pageTitle={t('stores.title')} pageSubtitle={t('stores.subtitle')}>
                    <StoresPage />
                  </AppLayout>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/view" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <AppLayout pageTitle={t('stores.title')} pageSubtitle={t('stores.subtitle')}>
                    <StoreHubRedirect />
                  </AppLayout>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/edit" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Update Store"
                    pageSubtitle="Modify store information and settings"
                    permissions={[Permissions.StoresUpdate]}
                    redirectTo={getStoreManagementRedirect}
                  >
                    <StoreUpdatePage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/payment-settings" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Payment Settings"
                    pageSubtitle="Manage subscription and customer payment settings"
                    permissions={[Permissions.StoresUpdate]}
                    ownerAlsoAllowed
                    redirectTo={getStoreManagementRedirect}
                  >
                    <PaymentSettingsPage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/communication" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Communication Settings"
                    pageSubtitle="Configure email and SMS notifications for appointment events"
                    permissions={[Permissions.StoresNotificationSettingsUpdate]}
                    ownerAlsoAllowed
                    redirectTo={getStoreManagementRedirect}
                  >
                    <CommunicationSettingsPage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/waitlist" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <AppLayout pageTitle={t('stores.title')} pageSubtitle={t('stores.subtitle')}>
                    <StoreHubRedirect tab="waitlist" />
                  </AppLayout>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/import-data" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Import data"
                    pageSubtitle="Import customers and other data from Fresha"
                    accessCheck={({ isOwner, managementUser }) => {
                      const userType = managementUser?.userType || managementUser?.user_type;
                      const isAppAdmin = userType === 1 || userType === '1' || userType === 'AppAdmin' || userType === 'appAdmin';
                      return isOwner || isAppAdmin;
                    }}
                    redirectTo={getStoreManagementRedirect}
                  >
                    <DataImportPage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/employee-payments/payroll" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <PlanFeatureGuard flag="allowsPayroll">
                  <ManagementRoute
                    pageTitle="Payroll"
                    pageSubtitle="Preview employee compensation for the selected month"
                    skeletonVariant="cards"
                    permissions={[Permissions.EmployeePaymentsRead, Permissions.EmployeePaymentsReadOwn]}
                    redirectTo={getStoreManagementRedirect}
                  >
                    <EmployeePayrollDashboardPage />
                  </ManagementRoute>
                  </PlanFeatureGuard>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/profile" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Profile"
                    pageSubtitle="Manage your profile and time off"
                    permissions={[Permissions.TeamMembersRead, Permissions.TeamMembersReadOwn]}
                    redirectTo={getStoreManagementRedirect}
                  >
                    <OwnerProfilePage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/team-members" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Team Members"
                    pageSubtitle="Manage your store team members"
                    permissions={[Permissions.TeamMembersRead]}
                    redirectTo={getStoreManagementRedirect}
                  >
                    <TeamMembersPage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/services" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <AppLayout pageTitle="Services" pageSubtitle="Manage your store services">
                    <ServicesPage />
                  </AppLayout>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/products" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <PlanFeatureGuard flag="allowsProducts">
                  <ManagementRoute
                    pageTitle="Products"
                    pageSubtitle="Manage your store products"
                    permissions={[Permissions.ProductsRead, Permissions.StockRead]}
                    redirectTo={getStoreManagementRedirect}
                  >
                    <ProductsPage />
                  </ManagementRoute>
                  </PlanFeatureGuard>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/stock" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <StockToProductsRedirect />
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/expenses" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <PlanFeatureGuard flag="allowsExpenses">
                  <ManagementRoute
                    pageTitle={t('navigation.expenses')}
                    pageSubtitle={t('expenses.subtitle')}
                    permissions={[Permissions.ExpensesRead]}
                    redirectTo={getStoreManagementRedirect}
                  >
                    <ExpensesPage />
                  </ManagementRoute>
                  </PlanFeatureGuard>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/membership-plans" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <PlanFeatureGuard flag="allowsMemberships">
                  <ManagementRoute
                    pageTitle="Membership Plans"
                    pageSubtitle="Manage your store membership plans"
                    permissions={[Permissions.MembershipsRead]}
                    redirectTo={getStoreManagementRedirect}
                  >
                    <MembershipPlansPage />
                  </ManagementRoute>
                  </PlanFeatureGuard>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/customer-memberships" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <PlanFeatureGuard flag="allowsMemberships">
                  <ManagementRoute
                    pageTitle="Customer Memberships"
                    pageSubtitle="Manage customer memberships and in-store payments"
                    permissions={[Permissions.MembershipsRead]}
                    redirectTo={getStoreManagementRedirect}
                  >
                    <StoreCustomerMembershipsPage />
                  </ManagementRoute>
                  </PlanFeatureGuard>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/customers/:customerId" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Customer details"
                    pageSubtitle="View store-specific customer details"
                    permissions={[Permissions.CustomersRead]}
                    redirectTo={getStoreManagementRedirect}
                  >
                    <StoreCustomerDetailsPage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/customers" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Customers"
                    pageSubtitle="View and add customers for your store"
                    permissions={[Permissions.CustomersRead]}
                    redirectTo={getStoreManagementRedirect}
                  >
                    <StoreCustomersPage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/notifications" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Notifications"
                    pageSubtitle="View your in-app notifications"
                    redirectTo={getStoreManagementRedirect}
                  >
                    <StaffNotificationsPage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/team-members/register" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <PlanFeatureGuard flag="allowsExtraTeamMembers">
                  <ManagementRoute
                    pageTitle="Add New Team Member"
                    pageSubtitle="Create a new team member account for your store"
                    permissions={[Permissions.TeamMembersCreate]}
                    redirectTo={getTeamMembersRedirect}
                  >
                    <TeamMemberRegistrationPage />
                  </ManagementRoute>
                  </PlanFeatureGuard>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/team-members/:teamMemberId" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Team Member"
                    pageSubtitle="View team member details"
                    permissions={[Permissions.TeamMembersRead]}
                    ownPermissions={[Permissions.TeamMembersReadOwn]}
                    ownResourceIdParam="teamMemberId"
                    redirectTo={getTeamMembersRedirect}
                  >
                    <TeamMemberViewPage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/team-members/:teamMemberId/edit" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Update Team Member"
                    pageSubtitle="Modify team member information and settings"
                    permissions={[Permissions.TeamMembersUpdate]}
                    ownPermissions={[Permissions.TeamMembersUpdateOwn]}
                    ownResourceIdParam="teamMemberId"
                    redirectTo={getTeamMembersRedirect}
                  >
                    <TeamMemberUpdatePage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/team-members/:teamMemberId/agenda" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Team Member Agenda"
                    pageSubtitle="Manage working hours, special openings and time off"
                    skeletonVariant="form"
                    permissions={[Permissions.TeamMembersUpdate]}
                    redirectTo={getTeamMembersRedirect}
                  >
                    <TeamMemberAgendaPage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/team-members/:teamMemberId/compensation" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <PlanFeatureGuard flag="allowsPayroll">
                  <ManagementRoute
                    pageTitle="Team Member Compensation"
                    pageSubtitle="Manage payment settings for this team member"
                    skeletonVariant="form"
                    permissions={[Permissions.EmployeePaymentsSettingsUpdate]}
                    ownPermissions={[Permissions.EmployeePaymentsReadOwn]}
                    ownResourceIdParam="teamMemberId"
                    redirectTo={getTeamMembersRedirect}
                  >
                    <TeamMemberCompensationPage />
                  </ManagementRoute>
                  </PlanFeatureGuard>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores/:storeId/roles" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <AppLayout pageTitle={t('stores.title')} pageSubtitle={t('stores.subtitle')}>
                    <StoreHubRedirect tab="roles" />
                  </AppLayout>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/team-members" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <AppLayout pageTitle="Team Members" pageSubtitle="Select a store to manage team members">
                    <TeamMembersFallbackPage />
                  </AppLayout>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/services" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <AppLayout pageTitle="Services" pageSubtitle="Select a store to manage services">
                    <ServicesFallbackPage />
                  </AppLayout>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/products" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <PlanFeatureGuard flag="allowsProducts">
                  <AppLayout pageTitle="Products" pageSubtitle="Select a store to manage products">
                    <ProductsFallbackPage />
                  </AppLayout>
                  </PlanFeatureGuard>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stock" element={<StockToProductsRedirect />} />
              <Route path="/management/expenses" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <PlanFeatureGuard flag="allowsExpenses">
                  <AppLayout pageTitle={t('navigation.expenses')} pageSubtitle={t('expenses.fallback.subtitle')}>
                    <ExpensesFallbackPage />
                  </AppLayout>
                  </PlanFeatureGuard>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/membership-plans" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <PlanFeatureGuard flag="allowsMemberships">
                  <AppLayout pageTitle="Membership Plans" pageSubtitle="Select a store to manage membership plans">
                    <MembershipPlansFallbackPage />
                  </AppLayout>
                  </PlanFeatureGuard>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/customers" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <AppLayout pageTitle="Customers" pageSubtitle="Select a store to manage customers">
                    <CustomersFallbackPage />
                  </AppLayout>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/payment-operations" element={
                <UserTypeProtectedRoute allowedUserTypes={[1]} redirectTo="/management/app-admin/login">
                  <AppLayout>
                    <PaymentOperationsPage />
                  </AppLayout>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/support" element={
                <UserTypeProtectedRoute allowedUserTypes={[1, 2]} redirectTo="/management/team-member/login">
                  <ManagementRoute
                    pageTitle="Support"
                    permissions={[Permissions.Chat]}
                    redirectTo="/management"
                  >
                    <SupportInboxPage />
                  </ManagementRoute>
                </UserTypeProtectedRoute>
              } />
              <Route path="/management/stores-management" element={
                <UserTypeProtectedRoute allowedUserTypes={[1]} redirectTo="/management/app-admin/login">
                  <AppLayout>
                    <StoresManagementPage />
                  </AppLayout>
                </UserTypeProtectedRoute>
              } />
              <Route
                path="/management/plan-discount-vouchers"
                element={<Navigate to="/management/stores-management?tab=vouchers" replace />}
              />

              {/* Not Found Page */}
              <Route path="/not-found" element={<NotFoundPage />} />

              {/* Legal pages */}
              <Route path="/l/:token" element={<ShortLinkRedirectPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              
              {/* Public landing page */}
              <Route path="/" element={<LandingPage />} />
              {renderLocalizedMarketingRoutes()}
              
              {/* Default redirect for unknown routes */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            )}
            <CookieConsentBanner />
            <PwaPullToReload />
            <ToastContainer />
            </SupportChatProvider>
            </StaffRealtimeProvider>
          </Router>
            </CookieConsentProvider>
          </ToastProvider>
        </AppDataProvider>
      </AuthProvider>
    </BrandThemeProvider>
  );
}

export default App;
